import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMRTState } from '../../../../hooks/useMRTState';
import * as API from '../../../../api/pocos';
import { mensagemErro } from './valores';
import { obterInicioEFimDoMes } from '../../../../utils/financeiro';
import { format } from 'date-fns';

export function consultaPocos({ bemId, recurso, columnFilters, columnFilterFns, globalFilter, sorting, columns, periodo }) {
    // Modos escolhidos pelo usuário prevalecem; descritores fornecem os padrões.
    const modos = Object.fromEntries(columns.map(c => [c.accessorKey, c.filterFn || 'contains']));
    return {
        produtoBensId: bemId, filters: columnFilters,
        filtersMode: { ...modos, ...columnFilterFns }, globalFilter, sorting,
        ...(recurso === 'medicoes' ? periodo : {}),
    };
}
export function useListagemPocos({ recurso, bem, parametro, columns, autorizado }) {
    const empresaId = localStorage.getItem('empresaId') || '';
    const pessoaId = localStorage.getItem('pessoaId') || '';
    const mrt = useMRTState({ storageKey: `Pocos.mrt2.${empresaId}.${pessoaId}.${bem.id}.${recurso}`, pageSizeDefault: Math.min(Number(parametro?.paginacao) || 10, 200) });
    const { columnFilters, columnFilterFns, globalFilter, sorting, pagination,
        setPagination, setRowCount, setIsLoading, setIsRefetching, setIsError } = mrt;
    const [rows, setRows] = useState([]), [erro, setErro] = useState('');
    const [periodo, setPeriodo] = useState(() => {
        if (recurso !== 'medicoes') return { dataInicio: '', dataFim: '' };
        const { primeiroDia, ultimoDia } = obterInicioEFimDoMes(new Date());
        const dataInput = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)
            ? value.slice(0, 10) : format(value, 'yyyy-MM-dd');
        return { dataInicio: dataInput(primeiroDia), dataFim: dataInput(ultimoDia) };
    });
    const request = useRef(0), carregou = useRef(false), filtrosRef = useRef('');
    const consulta = useMemo(() => consultaPocos({ bemId: bem.id, recurso, columnFilters, columnFilterFns, globalFilter, sorting, columns, periodo }),
        [bem.id, recurso, columnFilters, columnFilterFns, globalFilter, sorting, columns, periodo]);
    const erroPeriodo = periodo.dataInicio && periodo.dataFim && periodo.dataInicio > periodo.dataFim
        ? 'A data inicial deve ser anterior ou igual à data final.' : '';
    const carregar = useCallback(async () => {
        const atual = ++request.current;
        if (!autorizado) return;
        setIsLoading(!carregou.current); setIsRefetching(carregou.current); setErro('');
        try {
            if (erroPeriodo) throw new Error(erroPeriodo);
            const size = Math.min(pagination.pageSize, 200);
            const data = await API.consultar(`${recurso}/listar`, { ...consulta, start: pagination.pageIndex * size, size });
            if (atual !== request.current) return;
            if (!Array.isArray(data.dados) || !Number.isFinite(Number(data.total))) throw new Error('Resposta inválida na consulta.');
            const total = Number(data.total), ultima = Math.max(0, Math.ceil(total / size) - 1);
            if (pagination.pageIndex > ultima) { setPagination(p => ({ ...p, pageIndex: ultima })); return; }
            setRows(data.dados); setRowCount(total); setIsError(false); carregou.current = true;
        } catch (e) {
            if (atual === request.current) { setRows([]); setRowCount(0); setIsError(true); setErro(mensagemErro(e)); }
        } finally {
            if (atual === request.current) { setIsLoading(false); setIsRefetching(false); }
        }
    }, [autorizado, recurso, consulta, erroPeriodo, pagination.pageIndex, pagination.pageSize, setPagination, setRowCount, setIsLoading, setIsRefetching, setIsError]);
    useEffect(() => {
        if (!autorizado) { setRows([]); return; }
        const chave = JSON.stringify(consulta);
        const mudou = filtrosRef.current && filtrosRef.current !== chave;
        filtrosRef.current = chave;
        if (mudou && pagination.pageIndex !== 0) { setPagination(p => ({ ...p, pageIndex: 0 })); return; }
        if (pagination.pageSize > 200) { setPagination(p => ({ ...p, pageSize: 200, pageIndex: 0 })); return; }
        carregar();
        return () => { request.current += 1; };
    }, [autorizado, consulta, pagination.pageIndex, pagination.pageSize, setPagination, carregar]);
    function limpar() {
        mrt.setColumnFilters([]); mrt.setColumnFiltersMode({}); mrt.setGlobalFilter(''); mrt.setSorting([]);
        setPeriodo({ dataInicio: '', dataFim: '' }); setPagination(p => ({ ...p, pageIndex: 0 }));
    }
    return { mrt, rows, erro, setErro, consulta, periodo, setPeriodo, erroPeriodo, carregar, limpar };
}
